import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Chip, CircularProgress, Alert, Link,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { adminClient } from '../data/adminClient';
import { LEVEL_OPTIONS } from '../../shared/entryOptions';
import { GRADES, ROLES, TSHIRT_SIZES, DIETARY_OPTIONS } from '../../shared/participantOptions';
import TestSchoolChip from '../TestSchoolChip';
import { toCsv } from '../../shared/utils/csvExport';
import ChipFilterRow from './ChipFilterRow';

const REGISTRATION_STATUS_LABELS = { 'not-started': 'Not started', 'parent-pending': 'Parent pending', complete: 'Complete' };
const REGISTRATION_STATUS_COLORS = { 'not-started': 'error', 'parent-pending': 'warning', complete: 'success' };

function registrationStatus(p) {
  if (!p.studentSectionComplete) return 'not-started';
  if (!p.parentSectionComplete) return 'parent-pending';
  return 'complete';
}

// dietary is stored as a comma-joined string of DIETARY_OPTIONS tags (plus,
// for "Other", free text appended as "Other: ...") — see participantOptions.js.
function parseDietaryTags(dietary) {
  return String(dietary || '').split(',').map(s => s.trim()).filter(Boolean);
}
function hasDietaryTag(dietary, tag) {
  return parseDietaryTags(dietary).some(t => tag === 'Other' ? t.startsWith('Other') : t === tag);
}

const BREAKDOWN_DIMENSIONS = [
  { id: 'dietary', label: 'Dietary / Allergies' },
  { id: 'grade', label: 'Grade' },
  { id: 'role', label: 'Role' },
  { id: 'experienceLevel', label: 'Experience Level' },
  { id: 'tshirtSize', label: 'T-Shirt Size' },
  { id: 'registration', label: 'Registration Status' },
  { id: 'payment', label: 'Payment Status' },
];

export default function ParticipantRegistrationsSection() {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('All');
  const [gradeFilter, setGradeFilter] = useState(() => new Set());
  const [roleFilter, setRoleFilter] = useState(() => new Set());
  const [experienceFilter, setExperienceFilter] = useState(() => new Set());
  const [tshirtFilter, setTshirtFilter] = useState(() => new Set());
  const [registrationFilter, setRegistrationFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortField, setSortField] = useState('lastName');
  const [sortDir, setSortDir] = useState('asc');
  const [breakdownDimension, setBreakdownDimension] = useState('dietary');
  const [pendingHidden, setPendingHidden] = useState(0); // rows hidden: school's payment pending

  const fetchParticipants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allParticipants, registrations] = await Promise.all([
        adminClient.getParticipants(),
        adminClient.getEventRegistrations(),
      ]);
      // Withdrawn schools already paid their (non-refundable) registration
      // fee but aren't attending — their dancers stay on the sheet for the
      // record, but shouldn't clutter this screening view. Still fully
      // visible on the School Payments page.
      // Schools still waiting on their registration payment (paymentPending,
      // set server-side) don't show here until paid or OK to Proceed.
      const withdrawnSchools = new Set(registrations.filter(r => r.status === 'Withdrawn').map(r => r.schoolName));
      const active = allParticipants.filter(p => !withdrawnSchools.has(p.schoolName));
      setParticipants(active.filter(p => !p.paymentPending));
      setPendingHidden(active.filter(p => p.paymentPending).length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchParticipants(); }, [fetchParticipants]);

  const schoolNames = useMemo(() => [...new Set(participants.map(p => p.schoolName))].filter(Boolean).sort(), [participants]);

  const toggleSetFilter = (setter) => (value) => setter(prev => {
    const next = new Set(prev);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  });
  const toggleGrade = toggleSetFilter(setGradeFilter);
  const toggleRole = toggleSetFilter(setRoleFilter);
  const toggleExperience = toggleSetFilter(setExperienceFilter);
  const toggleTshirt = toggleSetFilter(setTshirtFilter);

  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = participants.filter(p => {
      const status = registrationStatus(p);
      return (
        (!q || `${p.firstName} ${p.lastName} ${p.email} ${p.schoolName}`.toLowerCase().includes(q)) &&
        (schoolFilter === 'All' || p.schoolName === schoolFilter) &&
        (gradeFilter.size === 0 || gradeFilter.has(String(p.grade || ''))) &&
        (roleFilter.size === 0 || roleFilter.has(p.role)) &&
        (experienceFilter.size === 0 || experienceFilter.has(p.experienceLevel)) &&
        (tshirtFilter.size === 0 || tshirtFilter.has(p.tshirtSize)) &&
        (registrationFilter === 'all' || status === registrationFilter) &&
        (paymentFilter === 'all' || (paymentFilter === 'paid' ? p.orgParticipationFeePaid : !p.orgParticipationFeePaid))
      );
    });
    return [...list].sort((a, b) => {
      if (sortField === 'grade') {
        const cmp = (Number(a.grade) || 0) - (Number(b.grade) || 0);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      if (sortField === 'payment') {
        const cmp = (a.orgParticipationFeePaid === b.orgParticipationFeePaid) ? 0 : a.orgParticipationFeePaid ? 1 : -1;
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const av = sortField === 'lastName' ? `${a.lastName} ${a.firstName}` : (a[sortField] || '');
      const bv = sortField === 'lastName' ? `${b.lastName} ${b.firstName}` : (b[sortField] || '');
      const cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [participants, search, schoolFilter, gradeFilter, roleFilter, experienceFilter, tshirtFilter, registrationFilter, paymentFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const hasActiveFilters = !!search || schoolFilter !== 'All' || gradeFilter.size > 0 || roleFilter.size > 0 ||
    experienceFilter.size > 0 || tshirtFilter.size > 0 || registrationFilter !== 'all' || paymentFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setSchoolFilter('All');
    setGradeFilter(new Set());
    setRoleFilter(new Set());
    setExperienceFilter(new Set());
    setTshirtFilter(new Set());
    setRegistrationFilter('all');
    setPaymentFilter('all');
  };

  // Always computed off displayRows, so filtering down to e.g. one school
  // first and then breaking down by dietary answers "how many vegetarians
  // at this school" just as naturally as "how many overall."
  // Test-school participants (isTest) are listed but never counted.
  const countedRows = useMemo(() => displayRows.filter(p => !p.isTest), [displayRows]);
  const countedTotal = useMemo(() => participants.filter(p => !p.isTest).length, [participants]);
  const testShown = displayRows.length - countedRows.length;

  const breakdownCounts = useMemo(() => {
    if (breakdownDimension === 'dietary') {
      const tagCounts = DIETARY_OPTIONS.map(opt => ({
        label: opt,
        count: countedRows.filter(p => hasDietaryTag(p.dietary, opt)).length,
      }));
      const notProvided = countedRows.filter(p => !p.dietary.trim()).length;
      return notProvided > 0 ? [...tagCounts, { label: 'Not yet provided', count: notProvided }] : tagCounts;
    }
    if (breakdownDimension === 'tshirtSize') {
      const tagCounts = TSHIRT_SIZES.map(opt => ({
        label: opt,
        count: countedRows.filter(p => p.tshirtSize === opt).length,
      }));
      const notProvided = countedRows.filter(p => !p.tshirtSize).length;
      return notProvided > 0 ? [...tagCounts, { label: 'Not yet provided', count: notProvided }] : tagCounts;
    }
    if (breakdownDimension === 'grade') {
      const tagCounts = GRADES.map(opt => ({
        label: opt,
        count: countedRows.filter(p => String(p.grade || '') === opt).length,
      }));
      const notProvided = countedRows.filter(p => !p.grade).length;
      return notProvided > 0 ? [...tagCounts, { label: 'Not yet provided', count: notProvided }] : tagCounts;
    }
    if (breakdownDimension === 'registration') {
      return ['not-started', 'parent-pending', 'complete'].map(key => ({
        label: REGISTRATION_STATUS_LABELS[key],
        count: countedRows.filter(p => registrationStatus(p) === key).length,
      }));
    }
    if (breakdownDimension === 'payment') {
      return [
        { label: 'Paid', count: countedRows.filter(p => p.orgParticipationFeePaid).length },
        { label: 'Not paid', count: countedRows.filter(p => !p.orgParticipationFeePaid).length },
      ];
    }
    // grade / role / experienceLevel / tshirtSize — simple exact-value tally,
    // largest first.
    const counts = new Map();
    countedRows.forEach(p => {
      const raw = p[breakdownDimension];
      const key = raw === null || raw === undefined || raw === '' ? 'Not set' : String(raw);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, count }));
  }, [countedRows, breakdownDimension]);

  const handleExportCSV = () => {
    const headers = ['First Name', 'Last Name', 'School', 'Grade', 'Role', 'Experience Level', 'T-Shirt Size', 'Dietary / Allergies', 'Registration Status', 'Payment Status', 'Email', 'Parent/Guardian Name', 'Parent/Guardian Email'];
    const data = displayRows.map(p => [
      p.firstName, p.lastName, p.schoolName, p.grade, p.role, p.experienceLevel, p.tshirtSize, p.dietary,
      REGISTRATION_STATUS_LABELS[registrationStatus(p)], p.orgParticipationFeePaid ? 'Paid' : 'Not paid',
      p.email, p.parentGuardianName, p.parentGuardianEmail,
    ]);
    const csv = toCsv([headers, ...data]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'participant-registrations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const TABLE_COLS = [
    { label: 'Name', field: 'lastName' },
    { label: 'School', field: 'schoolName' },
    { label: 'Grade', field: 'grade' },
    { label: 'Role', field: 'role' },
    { label: 'Experience', field: 'experienceLevel' },
    { label: 'T-Shirt', field: 'tshirtSize' },
    { label: 'Payment', field: 'payment' },
  ];

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.3, verticalAlign: 'middle', ml: 0.25 }} />;
    return sortDir === 'asc'
      ? <KeyboardArrowUpIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.25 }} />
      : <KeyboardArrowDownIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.25 }} />;
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search name, email, or school..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>School</InputLabel>
          <Select label="School" value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)}>
            <MenuItem value="All">All schools</MenuItem>
            {schoolNames.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Registration</InputLabel>
          <Select label="Registration" value={registrationFilter} onChange={e => setRegistrationFilter(e.target.value)}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="not-started">Not started</MenuItem>
            <MenuItem value="parent-pending">Parent pending</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Payment</InputLabel>
          <Select label="Payment" value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="not-paid">Not paid</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          disabled={displayRows.length === 0}
          sx={{ ml: 'auto' }}
        >
          Export CSV
        </Button>
      </Box>

      <ChipFilterRow label="Grade" options={GRADES} selected={gradeFilter} onToggle={toggleGrade} />
      <ChipFilterRow label="Role" options={ROLES} selected={roleFilter} onToggle={toggleRole} />
      <ChipFilterRow label="Experience" options={LEVEL_OPTIONS} selected={experienceFilter} onToggle={toggleExperience} />
      <ChipFilterRow label="T-Shirt" options={TSHIRT_SIZES} selected={tshirtFilter} onToggle={toggleTshirt} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {countedRows.length} of {countedTotal} participants
        {testShown > 0 && ` (+ ${testShown} from test schools, not counted)`}
        {pendingHidden > 0 && ` · ${pendingHidden} more hidden until their school's registration payment is received`}
        {hasActiveFilters && (
          <>
            {' · '}
            <Link component="button" variant="body2" underline="hover" onClick={clearFilters} sx={{ cursor: 'pointer' }}>
              Clear filters
            </Link>
          </>
        )}
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>Breakdown</Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Break down by</InputLabel>
              <Select label="Break down by" value={breakdownDimension} onChange={e => setBreakdownDimension(e.target.value)}>
                {BREAKDOWN_DIMENSIONS.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {breakdownCounts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No data for the current filters.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {breakdownCounts.map(({ label, count }) => (
                <Chip key={label} label={`${label}: ${count}`} variant="outlined" size="small" />
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: 'block' }}>
            Based on the {countedRows.length} participant{countedRows.length === 1 ? '' : 's'} (test schools excluded) currently matching the filters above — narrow those down first (e.g. to one school) to break down just that subset.
          </Typography>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              {TABLE_COLS.map(({ label, field }) => (
                <TableCell key={field} onClick={() => handleSort(field)}
                  sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {label}<SortIcon field={field} />
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600 }}>Dietary / Allergies</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Registration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {participants.length === 0 ? 'No participants registered yet.' : 'No matching participants.'}
                </TableCell>
              </TableRow>
            )}
            {displayRows.map(p => {
              const status = registrationStatus(p);
              const dietaryTags = parseDietaryTags(p.dietary).filter(t => t !== 'None');
              return (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight={500}>{p.firstName} {p.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{p.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.schoolName}{p.isTest && <TestSchoolChip />}</TableCell>
                  <TableCell>{p.grade || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.role || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.experienceLevel || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.tshirtSize || '—'}</TableCell>
                  <TableCell>
                    {p.orgParticipationFeePaid
                      ? <Chip label="Paid" color="success" size="small" />
                      : <Chip label="Not paid" color="warning" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {dietaryTags.length === 0 ? (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {dietaryTags.map(tag => <Chip key={tag} label={tag} size="small" variant="outlined" />)}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={REGISTRATION_STATUS_LABELS[status]} color={REGISTRATION_STATUS_COLORS[status]} size="small"
                      variant={status === 'complete' ? 'filled' : 'outlined'} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
